import * as React from 'react';
import { PersonSimpleCircle } from '@phosphor-icons/react';
import { cn } from '@relay-towing/tokens';
import {
  presencePillRecipe,
  presencePillStatusRecipe,
} from '../recipes/mobile.recipe';
import { StatusDot } from './Chip';
import { Icon } from './Icon';
import type { StatusTone } from './status-tone';
import { Text } from './Text';

export interface PresencePillProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /** The member's name, as the rest of the app shows it. */
  name: string;
  /** Their duty status in words: "On duty", "On a call", "Off duty". */
  status: string;
  /** The status dot's colour. Neutral when left off. */
  statusTone?: StatusTone;
}

/**
 * Who you are and whether you are working, centred along the top of every
 * tab. Tapping it opens the status picker and the menu behind it: settings,
 * the company switcher, the sections that are not tabs.
 *
 *   <PresencePill name="Marcus Webb" status="On duty" statusTone="success"
 *     onClick={openMenu} />
 *
 * The Figma's App Header: the name in a faint wash, the status in a white
 * chip at the end. The status is words as well as a dot, so it never rests
 * on the colour.
 */
export const PresencePill = React.forwardRef<
  HTMLButtonElement,
  PresencePillProps
>(function PresencePill(
  { name, status, statusTone = 'neutral', className, ...props },
  ref
) {
  return (
    <button
      ref={ref}
      type="button"
      className={cn(presencePillRecipe(), className)}
      {...props}
    >
      {/* A person glyph, never a photo: the Figma's App Header, and a pill
          that looks the same for everyone however their login was made. */}
      <Icon icon={PersonSimpleCircle} weight="fill" size="md" tone="muted" />
      <Text as="span" variant="body-strong" truncate>
        {name}
      </Text>
      <span className={presencePillStatusRecipe()}>
        <StatusDot tone={statusTone} size="chip" />
        <Text as="span" variant="body-sm-strong">
          {status}
        </Text>
      </span>
    </button>
  );
});
